import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Plus, Sparkles, X, User, Loader2 } from 'lucide-react';
import FollowUpSheet from './FollowUpSheet';
import { OUTCOME_OPTS, OUTCOME_LABEL, OUTCOME_TONE, QUICK_OUTCOMES, fmtFollowDate } from './constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Follow-up block for a trip- or lead-detail screen:
 *   • auto-SUGGESTION chip (confirm → schedule sheet, or dismiss)
 *   • "+ Schedule Follow-up" button
 *   • list of follow-ups with outcome quick-buttons + outcome log
 */
const FollowUpManager = ({ tripId, leadId }) => {
  const [items, setItems] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [prefillDate, setPrefillDate] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const qs = tripId ? `trip_id=${tripId}` : `lead_id=${leadId}`;

  const load = useCallback(async () => {
    if (!tripId && !leadId) return;
    try {
      const [li, sg] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/followups?${qs}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/followups/suggestion?${qs}`, { withCredentials: true }),
      ]);
      setItems(Array.isArray(li.data) ? li.data : []);
      setSuggestion(sg.data?.suggestion || null);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [qs, tripId, leadId]);

  useEffect(() => { load(); }, [load]);

  const setOutcome = async (id, outcome) => {
    setBusyId(id);
    try {
      await axios.put(`${BACKEND_URL}/api/followups/${id}/outcome`, { outcome }, { withCredentials: true });
      toast.success(`Marked: ${OUTCOME_LABEL[outcome]}`);
      await load();
    } catch { toast.error('Could not update outcome'); } finally { setBusyId(null); }
  };

  const dismissSuggestion = async () => {
    const body = tripId ? { trip_id: tripId } : { lead_id: leadId };
    body.rule = suggestion?.rule;
    setSuggestion(null);
    try { await axios.post(`${BACKEND_URL}/api/followups/suggestion/dismiss`, body, { withCredentials: true }); } catch { /* noop */ }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading follow-ups…</div>;
  }

  return (
    <div data-testid="followup-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
          <CalendarClock className="h-4 w-4 text-teal-700" /> Follow-ups
        </h3>
        <button type="button" onClick={() => { setPrefillDate(null); setSheetOpen(true); }} data-testid="schedule-followup-btn"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-teal-700 hover:text-teal-900">
          <Plus className="h-4 w-4" /> Schedule
        </button>
      </div>

      {/* Auto-suggestion */}
      {suggestion && (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="followup-suggestion">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-amber-800">Follow-up suggested</p>
              <p className="text-[12px] text-amber-700">{suggestion.reason}</p>
              <div className="flex items-center gap-2 mt-2">
                <button type="button" onClick={() => { setPrefillDate(suggestion.default_date); setSheetOpen(true); }} data-testid="followup-suggestion-confirm"
                  className="h-8 px-3 rounded-lg bg-amber-600 text-white text-[12px] font-semibold">Confirm</button>
                <button type="button" onClick={dismissSuggestion} data-testid="followup-suggestion-dismiss"
                  className="h-8 px-3 rounded-lg bg-white border border-amber-200 text-amber-700 text-[12px] font-semibold inline-flex items-center gap-1">
                  <X className="h-3 w-3" /> Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Existing follow-ups */}
      {items.length === 0 ? (
        <p className="text-[12px] text-slate-400 py-1" data-testid="followup-empty">No follow-ups yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((f) => {
            const pending = f.outcome === 'pending';
            return (
              <div key={f.id} className="rounded-xl border border-slate-100 bg-white p-3" data-testid={`followup-item-${f.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-medium text-slate-800 flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                    {fmtFollowDate(f.scheduled_date, f.scheduled_time)}
                  </span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${OUTCOME_TONE[f.outcome] || OUTCOME_TONE.pending}`} data-testid={`followup-outcome-${f.id}`}>
                    {OUTCOME_LABEL[f.outcome] || f.outcome}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                  <User className="h-3 w-3" /> {f.assigned_to_name || 'Unassigned'}
                  {f.notes ? <span className="truncate"> · {f.notes}</span> : null}
                </div>
                {pending ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {QUICK_OUTCOMES.map((o) => (
                      <button key={o} type="button" disabled={busyId === f.id} onClick={() => setOutcome(f.id, o)} data-testid={`followup-set-${o}-${f.id}`}
                        className="h-7 px-2.5 rounded-lg border border-slate-200 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                        {OUTCOME_LABEL[o]}
                      </button>
                    ))}
                  </div>
                ) : (
                  <select value={f.outcome} onChange={(e) => setOutcome(f.id, e.target.value)} disabled={busyId === f.id} data-testid={`followup-outcome-select-${f.id}`}
                    className="mt-2 h-8 px-2 rounded-lg border border-slate-200 text-[11px] bg-white">
                    {OUTCOME_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      <FollowUpSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tripId={tripId}
        leadId={leadId}
        prefillDate={prefillDate}
        onSaved={() => { setSuggestion(null); load(); }}
      />
    </div>
  );
};

export default FollowUpManager;
